import { useMemo, useRef, useState } from 'react';
import type { Money } from '../lib/api.js';
import { formatCompact, formatExact } from '../components/MoneyText.js';

/**
 * Value at each statement, drawn honestly.
 *
 * These are discrete observations on the dates statements were issued — the
 * line between them is interpolation and is drawn thin so the DOTS read as the
 * data. Invested cost rides along as a dashed reference line, so the gap
 * between the two IS the gain, visible without a single number.
 *
 * Specs follow the dataviz method: 2px line with round joins, ≥8px markers
 * carrying a 2px surface ring, an area wash at 10%, hairline solid gridlines,
 * a crosshair tooltip that snaps to the nearest statement (readers aim at a
 * date, not at a 2px line), and an endpoint label only — the tooltip and the
 * statement list below carry the rest. One y-axis, always.
 */
export interface ChartPoint {
  asOf: string;
  value: Money;
  invested: Money | null;
}

const W = 640;
const H = 180;
const PAD = { top: 14, right: 64, bottom: 22, left: 8 };

/** Clean y-axis steps: 1/2/5 × 10^n covering the range in ~3 ticks. */
function ticksFor(max: number): number[] {
  if (max <= 0) return [0];
  const raw = max / 3;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((k) => k * pow).find((s) => s >= raw) ?? raw;
  const out: number[] = [0];
  /*
   * The top tick must REACH OR PASS the maximum: the scale's ceiling is the
   * last tick, and a ceiling below the data sent the biggest values above the
   * plot — the line simply vanished off the top of its own chart.
   */
  while (out.at(-1)! < max) out.push(out.at(-1)! + step);
  return out;
}

export function StatementChart({ points }: { points: ChartPoint[] }) {
  const wrap = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const model = useMemo(() => {
    const xs = points.map((p) => Date.parse(p.asOf));
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const span = Math.max(1, x1 - x0);
    const maxY = Math.max(...points.map((p) => Math.max(p.value.amount, p.invested?.amount ?? 0)));
    const ticks = ticksFor(maxY);
    const top = ticks.at(-1) ?? maxY;
    const px = (t: number) => PAD.left + ((t - x0) / span) * (W - PAD.left - PAD.right);
    const py = (v: number) => PAD.top + (1 - v / top) * (H - PAD.top - PAD.bottom);
    const pts = points.map((p, i) => ({
      i,
      x: points.length === 1 ? (PAD.left + W - PAD.right) / 2 : px(xs[i]!),
      y: py(p.value.amount),
      yi: p.invested ? py(p.invested.amount) : null,
      p,
    }));
    return { pts, ticks, top, py };
  }, [points]);

  const line = model.pts.map((d, i) => `${i === 0 ? 'M' : 'L'}${d.x.toFixed(1)},${d.y.toFixed(1)}`).join(' ');
  const area = `${line} L${model.pts.at(-1)!.x.toFixed(1)},${H - PAD.bottom} L${model.pts[0]!.x.toFixed(1)},${H - PAD.bottom} Z`;
  const invested = model.pts
    .filter((d) => d.yi !== null)
    .map((d, i) => `${i === 0 ? 'M' : 'L'}${d.x.toFixed(1)},${d.yi!.toFixed(1)}`)
    .join(' ');
  const last = model.pts.at(-1)!;

  /** Snap the pointer to the nearest statement date (crosshair finds the X). */
  function onMove(e: React.PointerEvent) {
    const box = wrap.current?.getBoundingClientRect();
    if (!box) return;
    const x = ((e.clientX - box.left) / box.width) * W;
    let best = 0;
    for (let i = 1; i < model.pts.length; i += 1) {
      if (Math.abs(model.pts[i]!.x - x) < Math.abs(model.pts[best]!.x - x)) best = i;
    }
    setHover(best);
  }

  const h = hover === null ? null : model.pts[hover]!;

  return (
    <div
      ref={wrap}
      className="chart"
      onPointerMove={onMove}
      onPointerLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Value at each statement" style={{ display: 'block', width: '100%' }}>
        {/* recessive hairline grid; the topmost tick doubles as the axis cap */}
        {model.ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={model.py(t)}
              y2={model.py(t)}
              className="chart-grid"
            />
            <text x={W - PAD.right + 6} y={model.py(t) + 4} className="chart-tick">
              {t === 0 ? '0' : formatCompact(t).replace('₹', '')}
            </text>
          </g>
        ))}

        {/* the wash, then the line, then the reference, then the dots */}
        <path d={area} className="chart-area" />
        <path d={line} className="chart-line" />
        {invested && model.pts.length > 1 && <path d={invested} className="chart-invested" />}
        {model.pts.map((d) => (
          <circle key={d.i} cx={d.x} cy={d.y} r={4} className="chart-dot" />
        ))}

        {/* endpoint label — the one number worth printing on the plot */}
        <text x={last.x} y={last.y - 10} textAnchor={last.x > W - 120 ? 'end' : 'middle'} className="chart-endlabel">
          {formatCompact(last.p.value.amount)}
        </text>

        {/* first/last dates anchor the x-axis without a tick forest */}
        <text x={model.pts[0]!.x} y={H - 6} textAnchor="start" className="chart-tick">
          {model.pts[0]!.p.asOf}
        </text>
        {model.pts.length > 1 && (
          <text x={last.x} y={H - 6} textAnchor="end" className="chart-tick">
            {last.p.asOf}
          </text>
        )}

        {/* crosshair — snapped, with a lifted marker */}
        {h && (
          <g>
            <line x1={h.x} x2={h.x} y1={PAD.top} y2={H - PAD.bottom} className="chart-crosshair" />
            <circle cx={h.x} cy={h.y} r={5.5} className="chart-dot chart-dot-active" />
          </g>
        )}
      </svg>

      {h && (
        <div
          className="chart-tip"
          style={{
            left: `${(h.x / W) * 100}%`,
            transform: `translateX(${h.x > W * 0.7 ? '-100%' : h.x < W * 0.3 ? '0' : '-50%'})`,
          }}
        >
          {/* values lead, labels follow */}
          <strong>{formatExact(h.p.value.amount)}</strong>
          <span>
            <i className="chart-key chart-key-value" aria-hidden /> value · {h.p.asOf}
          </span>
          {h.p.invested && (
            <span>
              <i className="chart-key chart-key-invested" aria-hidden /> invested{' '}
              {formatCompact(h.p.invested.amount)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
